import React, { useEffect, useState } from 'react';

import starTokens from '@amzn/meridian-tokens/base/icon/star';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import { type TaskConflict } from 'src/services/jmr/types';
import { setIsTldAlertModalOpen, setJmrResults } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';
import {
  canJobBeModified,
  createDataCyForJob,
  isJobAtCapacity,
  JmrJobTypes,
  type TableDataRow,
} from 'src/utils/jmrUtils';
import { toggleJob } from 'src/utils/placementSelectionUtils';
import { filterTableRows } from 'src/utils/tableUtils';

import TldAlertModal from '../../tldAlertModal/tldAlertModal';

import ApplyModificationsModal from './applyModificationsModal';
import ConflictTag from './conflictPopover';

interface JobMatchesTableProps {
  searchField: string;
  jobsExceedingRestrictionsInAggregate: Set<string>;
  exceededRestrictionIds: Set<number>;
  /** For scrolling job row into view */
  selectedJobRefs: React.MutableRefObject<Map<string, HTMLElement>>;
  stickyHeight: number;
}

const JobMatchesTable = ({
  searchField,
  jobsExceedingRestrictionsInAggregate,
  exceededRestrictionIds,
  selectedJobRefs,
  stickyHeight,
}: JobMatchesTableProps) => {
  const { jmrFilter, jmrResults, backgroundFormData, tldAlertModal } = useAppSelector((state) => state.newJmrPage);
  const dispatch = useAppDispatch();
  const [tableData, setTableData] = useState(jmrResults);
  const [jobToModify, setJobToModify] = useState<null | TableDataRow>(null);

  useEffect(() => {
    // filter, sorting, and pagination
    const filteredData = filterTableRows(jmrResults, searchField, [
      'shiftId',
      'jobId',
      'totalHours',
      'perfectMatch',
      'isChecked',
      'rankingScore',
    ]).filter((row) => row.jobType === jmrFilter.jobType);
    setTableData(filteredData);
  }, [jmrResults, jmrFilter.jobType, searchField]);

  const setTotalHours = (id: string, totalHours: string) => {
    dispatch(
      setJmrResults(jmrResults.map((jmrResult) => (jmrResult.id === id ? { ...jmrResult, totalHours } : jmrResult))),
    );
  };

  const getTotalHoursErrorMsg = (row: TableDataRow) => {
    const totalSelectedHours = jmrResults
      .filter((r) => r.isChecked)
      .reduce((acc, row) => acc + (+row?.totalHours || 0), 0);

    const actualShiftLength = backgroundFormData.cannotWorkNormalShiftHrs
      ? +backgroundFormData.limitedHoursPerDay!
      : backgroundFormData.shiftLength;

    if (isNaN(+row.totalHours) || +row.totalHours <= 0) {
      return 'Must be a number greater than zero.';
    } else if (totalSelectedHours > actualShiftLength) {
      return 'Allocated hours exceed shift length';
    } else if (!canWorkFullShift(row) && +row.totalHours > row.maximumHoursAssociateCanDo) {
      return 'Allocated hours exceed restrictions';
    } else if (jobsExceedingRestrictionsInAggregate.has(row.id)) {
      return 'Allocated hours across multiple jobs exceed restrictions';
    }

    return null;
  };

  const canWorkFullShift = ({ conflicts }: TableDataRow) => {
    // either there are no conflicts, or the job has been modified
    return !conflicts?.length || conflicts.every((c) => c.appliedModifications?.length);
  };

  const onChecked = (resultId: string) => {
    if (jmrFilter.jobType === JmrJobTypes.TLD && !tldAlertModal.hasBeenOpened) {
      dispatch(
        setIsTldAlertModalOpen({
          open: true,
          resultId,
        }),
      );
    } else {
      toggleJob(resultId, jmrResults, dispatch);
    }
  };

  const onClickModify = (row: TableDataRow) => {
    if (jmrFilter.jobType === JmrJobTypes.TLD && !tldAlertModal.hasBeenOpened) {
      dispatch(
        setIsTldAlertModalOpen({
          open: true,
          resultId: row.id,
        }),
      );
    } else {
      setJobToModify(row);
    }
  };

  const onSaveModifications = (modifiedConflicts: TaskConflict[]) => {
    const modifiedJmrResults = jmrResults.map((row) => {
      if (jobToModify?.id === row.id) {
        const modifiedJob = { ...row, conflicts: modifiedConflicts };
        modifiedJob.isChecked = true;
        modifiedJob.disable = false;
        return modifiedJob;
      }
      return row;
    });
    dispatch(setJmrResults(modifiedJmrResults));
    setJobToModify(null);
    dispatch(
      createToast({
        type: 'success',
        message: 'Job was modified and selected',
        timeout: TOAST_TIMEOUT,
      }),
    );
  };

  const onConfirmTldModal = () => {
    const job = tableData.find((row) => row.id === tldAlertModal.resultId);
    if (!job) {
      return;
    }

    if (job.conflicts?.length && !job.maximumHoursAssociateCanDo) {
      setJobToModify(job);
    } else {
      toggleJob(tldAlertModal.resultId, jmrResults, dispatch);
    }
  };

  return (
    <>
      <Table
        layout="auto"
        fixHeaderRows
        fixHeaderRowsOffset={stickyHeight}
        headerRows={1}
        showStripes={true}
        spacing="small"
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        <TableRow>
          <TableCell sortColumn="rank">Job</TableCell>
          <TableCell sortColumn="name">Shift</TableCell>
          <TableCell>Conflicts</TableCell>
          <TableCell>Action</TableCell>
          <TableCell></TableCell>
          <TableCell width={80}></TableCell>
        </TableRow>
        {tableData.map((row, index) => {
          return (
            <TableRow key={index} highlightOnHover={true}>
              <TableCell>
                <Column spacing="200">
                  <Row spacing={'200'}>
                    <Text type="b300">{row.name}</Text>
                    {row.perfectMatch && (
                      <Tooltip
                        position="top"
                        title={'This job does not require any tasks the associate is restricted in'}
                      >
                        <Icon tokens={starTokens} />
                      </Tooltip>
                    )}
                  </Row>

                  <Text type="b200" color="secondary">
                    {row.jobType}
                  </Text>
                </Column>
              </TableCell>
              <TableCell>
                {!!row.shiftCode && (
                  <Column spacing="200">
                    <Text type="b300">{row.shiftCode}</Text>
                    <Text type="b200" color="secondary">
                      {`${row?.jobCapacity || 0} hrs capacity`}
                    </Text>
                  </Column>
                )}
              </TableCell>
              <TableCell>
                <Column spacing="200">
                  <ConflictTag jobMatch={row} {...{ exceededRestrictionIds }} />
                  <Text type="b200" color="secondary">
                    {canWorkFullShift(row) ? 'Full Shift' : `Maximum hrs: ${row.maximumHoursAssociateCanDo}`}
                  </Text>
                </Column>
              </TableCell>

              <TableCell>
                {canJobBeModified(row) ? (
                  <Button
                    type="tertiary"
                    data-cy={`${createDataCyForJob(row.name)}-modify`}
                    onClick={() => onClickModify(row)}
                    aria-label={`Modify ${row.name}`}
                  >
                    Modify
                  </Button>
                ) : (
                  <></>
                )}
              </TableCell>

              <TableCell>
                {row.disable || isJobAtCapacity(row) ? (
                  <Tooltip
                    title={
                      isJobAtCapacity(row)
                        ? 'Capacity is full.'
                        : 'Apply job modifications to enable selection of this job.'
                    }
                    position="top"
                  >
                    <Checkbox disabled={true} aria-label={`${row.name} Checkbox`} />
                  </Tooltip>
                ) : (
                  <Checkbox
                    checked={row.isChecked}
                    onChange={() => onChecked(row.id)}
                    data-cy={`${createDataCyForJob(row.name)}-checkbox`}
                    aria-label={`${row.name} Checkbox`}
                  />
                )}
              </TableCell>

              <TableCell width={80} alignmentVertical="top">
                <div className="jobMatchesTable__input-wrapper">
                  {row.isChecked ? (
                    <Input
                      ref={(ref) => ref && selectedJobRefs.current.set(row.id, ref)}
                      type="text"
                      value={row.totalHours.toString()}
                      onChange={(totalHours: string) => setTotalHours(row.id, totalHours)}
                      error={!!getTotalHoursErrorMsg(row)}
                      errorMessage={getTotalHoursErrorMsg(row)}
                      size="small"
                      placeholder="hrs..."
                      data-cy={`${createDataCyForJob(row.name)}-totalHours`}
                    />
                  ) : (
                    ''
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </Table>
      <TldAlertModal
        isOpen={tldAlertModal.open}
        setIsOpen={(open) =>
          dispatch(
            setIsTldAlertModalOpen({
              open,
            }),
          )
        }
        onConfirm={() => onConfirmTldModal()}
      />

      <ApplyModificationsModal
        {...{ jobToModify, exceededRestrictionIds }}
        isOpen={!!jobToModify}
        onCancel={() => setJobToModify(null)}
        onSaveAndApply={onSaveModifications}
      />
    </>
  );
};

export default JobMatchesTable;
